import React from "react";
import {
  buildCustomerCaiReadOnlyModel,
  isCustomerCaiEligible,
  type CustomerCaiReadOnlyModel,
} from "../../lib/currentAssetIntelligence/customerCaiReadOnlySurface";

type Props = {
  account: {
    status?: string | null;
    account_type?: string | null;
    accountType?: string | null;
    oeMode?: string | null;
    oe_mode?: string | null;
    local_config?: Record<string, unknown> | null;
    current_asset_intelligence_customer_enabled?: boolean;
  } | null;
  readModel?: unknown;
  state?: "ready" | "loading" | "error";
  presentation?: "full" | "brief";
};

const panel: React.CSSProperties = {
  border: "1px solid #dbeafe",
  borderRadius: 12,
  padding: 14,
  background: "#fff",
};

const compactCard: React.CSSProperties = {
  ...panel,
  padding: 11,
};

function DisclosureControl({ expanded, onToggle }: { expanded: boolean; onToggle: () => void }) {
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggle();
    }
  };
  return <div
    role="button"
    tabIndex={0}
    aria-expanded={expanded}
    onClick={onToggle}
    onKeyDown={onKeyDown}
    style={{ display: "inline-flex", alignItems: "center", minHeight: 30, marginTop: 9, padding: "5px 9px", border: "1px solid #bfdbfe", borderRadius: 999, color: "#1d4ed8", background: "#eff6ff", fontSize: 12, fontWeight: 800, cursor: "pointer", userSelect: "none" }}
  >{expanded ? "Hide details" : "View details"}</div>;
}

function CustomerCaiSummaryFirst({ model, accountStatus, compact = false }: { model: CustomerCaiReadOnlyModel; accountStatus?: string | null; compact?: boolean }) {
  const highestPriorityOpportunity = model.priorityOpportunities.find((item) => String(item.priority || "").toLowerCase() === "high")
    || model.priorityOpportunities[0];
  const primaryAssetGap = model.assetGaps[0] || highestPriorityOpportunity;
  const recommendedMove = model.recommendedNextActions[0]
    || highestPriorityOpportunity?.title
    || "Inspect the clearest reviewed signal before choosing the next area to strengthen.";
  const whyItMatters = highestPriorityOpportunity
    ? `${highestPriorityOpportunity.title} is the clearest area to strengthen in the reviewed account picture.`
    : model.recommendedNextActions[0]
      ? "This gives the next review a clear starting point."
      : "A clear next move will follow the strongest reviewed signal when one is available.";
  const nextAction = highestPriorityOpportunity
    ? { label: "What matters most", title: highestPriorityOpportunity.title, detail: highestPriorityOpportunity.detail }
    : model.recommendedNextActions[0]
      ? { label: "What to strengthen next", title: model.recommendedNextActions[0], detail: "This is the clearest next action in the reviewed account readout." }
      : { label: "What matters most", title: "Start with the clearest reviewed signal.", detail: "Inspect the supporting visibility evidence before choosing the next area to strengthen." };
  const reviewedInputCount = model.reviewedInputCount ?? model.assetsReviewed;
  const statusLabel = String(accountStatus || "review").trim() || "Review";
  const prescription = model.outputPrescription;
  if (compact) {
    return <section data-testid="customer-cai-summary-first" style={{ ...panel, marginTop: 10, padding: 12 }}>
      <div data-testid="customer-cai-recommended-output" style={{ marginTop: 9, paddingTop: 9, borderTop: "1px solid #dbeafe" }}>
        <div style={{ color: "#1d4ed8", fontSize: 11, fontWeight: 900, letterSpacing: "0.05em", textTransform: "uppercase" }}>Recommended output</div>
        <div style={{ marginTop: 3, color: "#172554", fontSize: 14, fontWeight: 900 }}>{prescription.outputType}</div>
      </div>
      <div data-testid="customer-cai-target-asset" style={{ marginTop: 9, paddingTop: 9, borderTop: "1px solid #dbeafe" }}>
        <div style={{ color: "#64748b", fontSize: 11, fontWeight: 900, letterSpacing: "0.05em", textTransform: "uppercase" }}>Target asset</div>
        <div style={{ marginTop: 3, color: "#334155", fontSize: 13, lineHeight: 1.4 }}>{prescription.targetAsset}</div>
      </div>
      <div data-testid="customer-cai-audience" style={{ marginTop: 9, paddingTop: 9, borderTop: "1px solid #dbeafe" }}>
        <div style={{ color: "#64748b", fontSize: 11, fontWeight: 900, letterSpacing: "0.05em", textTransform: "uppercase" }}>For</div>
        <div style={{ marginTop: 3, color: "#334155", fontSize: 13, lineHeight: 1.4 }}>{prescription.audience}</div>
      </div>
      <div data-testid="customer-cai-evidence-pattern" style={{ marginTop: 9, paddingTop: 9, borderTop: "1px solid #dbeafe" }}>
        <div style={{ color: "#64748b", fontSize: 11, fontWeight: 900, letterSpacing: "0.05em", textTransform: "uppercase" }}>Evidence signal</div>
        <div style={{ marginTop: 3, color: "#475569", fontSize: 13, lineHeight: 1.4 }}>{prescription.evidencePattern}</div>
      </div>
      <div data-testid="customer-cai-why-it-matters" style={{ marginTop: 9, paddingTop: 9, borderTop: "1px solid #dbeafe" }}>
        <div style={{ color: "#64748b", fontSize: 11, fontWeight: 900, letterSpacing: "0.05em", textTransform: "uppercase" }}>Why it matters</div>
        <div style={{ marginTop: 3, color: "#475569", fontSize: 13, lineHeight: 1.4 }}>{prescription.evidenceReason || primaryAssetGap?.detail || whyItMatters}</div>
      </div>
      <div data-testid="customer-cai-recommended-move" style={{ marginTop: 9, paddingTop: 9, borderTop: "1px solid #dbeafe" }}>
        <div style={{ color: "#1d4ed8", fontSize: 11, fontWeight: 900, letterSpacing: "0.05em", textTransform: "uppercase" }}>Next action</div>
        <div style={{ marginTop: 3, color: "#172554", fontSize: 13, fontWeight: 850, lineHeight: 1.4 }}>{prescription.primaryNextAction || recommendedMove}</div>
      </div>
      <div data-testid="customer-cai-expected-result" style={{ marginTop: 9, paddingTop: 9, borderTop: "1px solid #dbeafe" }}>
        <div style={{ color: "#64748b", fontSize: 11, fontWeight: 900, letterSpacing: "0.05em", textTransform: "uppercase" }}>Expected result</div>
        <div style={{ marginTop: 3, color: "#475569", fontSize: 13, lineHeight: 1.4 }}>{prescription.expectedResult}</div>
      </div>
      {prescription.actionBriefStatus ? <div data-testid="customer-cai-draft-readiness" style={{ marginTop: 8, color: "#64748b", fontSize: 11 }}>Draft readiness: {prescription.actionBriefStatus}</div> : null}
    </section>;
  }
  return <section data-testid="customer-cai-summary-first" style={{ ...panel, marginTop: 14, padding: 14 }}>
    <div style={{ color: "#2563eb", fontSize: 12, fontWeight: 900, letterSpacing: "0.06em", textTransform: "uppercase" }}>Current Asset Intelligence Summary</div>
    <div style={{ marginTop: 8, color: "#64748b", fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em" }}>Primary insight · What we found</div>
    <p style={{ margin: "6px 0 0", color: "#334155", lineHeight: 1.5 }}>{model.summary}</p>
    <div style={{ marginTop: 9, color: "#475569", fontSize: 13, lineHeight: 1.45 }}>
      Reviewed intelligence and action guidance: CAI distills reviewed findings into a decision brief showing what matters, why it matters, and where to focus next.
    </div>
    <div data-testid="customer-cai-readiness-summary" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 9, marginTop: 12 }}>
      <div style={{ ...compactCard, background: "#f8fbff" }}><div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>Account status</div><div style={{ marginTop: 5, fontWeight: 900, textTransform: "capitalize" }}>{statusLabel}</div></div>
      <div style={{ ...compactCard, background: "#f8fbff" }}><div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>Readiness</div><div style={{ marginTop: 5, fontWeight: 900 }}>{model.readiness}</div></div>
      <div style={{ ...compactCard, background: "#f8fbff" }}><div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>{model.reviewedInputLabel || "Signals reviewed"}</div><div style={{ marginTop: 5, fontWeight: 900 }}>{reviewedInputCount === null ? "Not available" : reviewedInputCount}</div></div>
      <div data-testid="customer-cai-reviewed-artifact-count" style={{ ...compactCard, background: "#f8fbff" }}><div style={{ fontSize: 12, color: "#64748b", fontWeight: 800 }}>Reviewed outputs available</div><div style={{ marginTop: 5, fontWeight: 900 }}>{model.reviewedOutputArtifacts.length}</div></div>
    </div>
    <div data-testid="customer-cai-primary-guidance" style={{ marginTop: 12, padding: 12, borderRadius: 10, background: "#eff6ff", border: "1px solid #bfdbfe" }}>
      <div style={{ color: "#1d4ed8", fontSize: 12, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.04em" }}>{nextAction.label} · Why it matters</div>
      <div style={{ marginTop: 4, fontSize: 16, fontWeight: 900, color: "#172554" }}>{nextAction.title}</div>
      <div style={{ marginTop: 4, color: "#334155", lineHeight: 1.45 }}>{nextAction.detail}</div>
      <div data-testid="customer-cai-recommended-move" style={{ marginTop: 9, paddingTop: 9, borderTop: "1px solid #bfdbfe" }}>
        <div style={{ color: "#1d4ed8", fontSize: 12, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.04em" }}>Recommended move</div>
        <div style={{ marginTop: 4, color: "#172554", fontWeight: 850, lineHeight: 1.4 }}>{recommendedMove}</div>
      </div>
      <div style={{ marginTop: 7, color: "#475569", fontSize: 12, lineHeight: 1.4 }}>{whyItMatters}</div>
    </div>
    <div style={{ marginTop: 9, color: "#64748b", fontSize: 12 }}>Current Asset Intelligence is read-only for this account view; completed durable briefs remain available from the relevant opportunity.</div>
  </section>;
}

type OePresentationMode = "competitive" | "local" | null;

function normalizeOePresentationMode(value: unknown): OePresentationMode {
  const normalized = String(value || "").trim().toLowerCase().replace(/[-\s]+/g, "_");
  if (normalized === "competitive" || normalized === "competitive_oe") return "competitive";
  if (normalized === "local" || normalized === "local_oe") return "local";
  return null;
}

function resolveOePresentationMode(account: Props["account"], readModel: unknown): OePresentationMode {
  const source = readModel && typeof readModel === "object" ? readModel as Record<string, unknown> : {};
  const candidates = [
    source.oeMode,
    source.oe_mode,
    source.presentationMode,
    source.presentation_mode,
    source.mode,
    account?.oeMode,
    account?.oe_mode,
    account?.account_type,
    account?.accountType,
    source.accountType,
    source.account_type,
  ];
  for (const candidate of candidates) {
    const mode = normalizeOePresentationMode(candidate);
    if (mode) return mode;
  }
  return null;
}

function CustomerCaiOeHandoff({ mode }: { mode: OePresentationMode }) {
  const modeCopy = mode === "competitive"
    ? "Competitive OE adds deeper competitor, category, entity, and market context when that view is available."
    : mode === "local"
      ? "Local OE presents simpler local-market visibility and action context when that view is available."
      : "Supporting visibility evidence provides deeper context behind a recommendation when that view is available.";
  return <section data-testid="customer-cai-oe-handoff" style={{ ...panel, marginTop: 12, padding: 13, background: "#f8fbff" }}>
    <h3 style={{ margin: 0, fontSize: 16 }}>Where to inspect supporting evidence</h3>
    <p style={{ margin: "7px 0 0", color: "#475569", lineHeight: 1.45 }}>CAI summarizes what matters most and frames the decision. OE contains the deeper evidence to inspect and the action paths to consider.</p>
    <p style={{ margin: "7px 0 0", color: "#475569", lineHeight: 1.45 }}>{modeCopy}</p>
    <p style={{ margin: "7px 0 0", color: "#475569", lineHeight: 1.45 }}>Reviewed CAI artifacts are the action layer. Use OE when you want to understand why a recommendation matters and what to strengthen next.</p>
  </section>;
}

function ItemList({ items, empty }: { items: CustomerCaiReadOnlyModel["priorityOpportunities"]; empty: string }) {
  if (!items.length) return <div style={{ color: "#64748b", lineHeight: 1.45 }}>{empty}</div>;
  return <div style={{ display: "grid", gap: 8 }}>{items.map((item, index) => <article key={`${item.title}-${index}`} style={panel}><div style={{ fontWeight: 800 }}>{item.title}</div><div style={{ marginTop: 4, color: "#475569", lineHeight: 1.45 }}>{item.detail}</div>{item.priority ? <div style={{ marginTop: 5, color: "#1d4ed8", fontSize: 12, fontWeight: 700 }}>Priority: {item.priority}</div> : null}</article>)}</div>;
}

function OutputRecommendationList({ items }: { items: CustomerCaiReadOnlyModel["outputRecommendations"] }) {
  if (!items.length) return null;
  return <section data-testid="customer-cai-output-recommendations" style={{ ...panel, marginTop: 12 }}>
    <h3 style={{ margin: 0, fontSize: 17 }}>Possible deliverables</h3>
    <div style={{ marginTop: 9, display: "grid", gap: 8 }}>
      {items.map((item, index) => <article key={`${item.title}-${index}`} style={panel}>
        <div style={{ fontWeight: 800 }}>{item.title}</div>
        <div style={{ marginTop: 4, color: "#1e40af", fontSize: 12, fontWeight: 700 }}>{item.outputType}</div>
        <div style={{ marginTop: 4, color: "#475569", lineHeight: 1.45 }}>{item.detail}</div>
        <div style={{ marginTop: 4, color: "#64748b", fontSize: 12 }}>Based on: {item.sourceInsight}</div>
        {item.status ? <div style={{ marginTop: 4, color: "#64748b", fontSize: 12 }}>Status: {item.status}</div> : null}
        {item.priority ? <div style={{ marginTop: 4, color: "#1d4ed8", fontSize: 12, fontWeight: 700 }}>Priority: {item.priority}</div> : null}
      </article>)}
    </div>
    <div style={{ marginTop: 9, color: "#64748b", fontSize: 12 }}>These recommendations are read-only account context; durable briefs are opened from the relevant opportunity.</div>
  </section>;
}

function ReviewedOutputPreviewList({ items }: { items: CustomerCaiReadOnlyModel["reviewedOutputPreviews"] }) {
  const [expandedIndexes, setExpandedIndexes] = React.useState<Set<number>>(() => new Set());
  const toggle = (index: number) => setExpandedIndexes((current) => {
    const next = new Set(current);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    return next;
  });
  if (!items.length) return null;
  return <section data-testid="customer-cai-reviewed-output-previews" style={{ ...panel, marginTop: 12 }}>
    <h3 style={{ margin: 0, fontSize: 17 }}>Reviewed output previews</h3>
    <div style={{ marginTop: 9, display: "grid", gap: 8 }}>
      {items.map((item, index) => {
        const expanded = expandedIndexes.has(index);
        return <article key={`${item.title}-${index}`} style={compactCard}>
        <div style={{ fontWeight: 800 }}>{item.title}</div>
        <div style={{ marginTop: 4, color: "#1e40af", fontSize: 12, fontWeight: 700 }}>{item.outputType}</div>
        <div style={{ marginTop: 4, color: "#64748b", fontSize: 12 }}>Linked to: {item.linkedRecommendationTitle}</div>
        <div style={{ marginTop: 6, color: "#475569", lineHeight: 1.45, display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: expanded ? undefined : 3, overflow: expanded ? "visible" : "hidden" }}>{item.summary}</div>
        {item.status ? <div style={{ marginTop: 6, color: "#64748b", fontSize: 12 }}>Status: {item.status}</div> : null}
        {item.priority ? <div style={{ marginTop: 4, color: "#1d4ed8", fontSize: 12, fontWeight: 700 }}>Priority: {item.priority}</div> : null}
        {item.sections.length ? <DisclosureControl expanded={expanded} onToggle={() => toggle(index)} /> : null}
        {expanded && item.sections.length ? <div data-testid={`customer-cai-reviewed-output-preview-details-${index}`} style={{ marginTop: 8, display: "grid", gap: 6 }}>
          {item.sections.map((section, sectionIndex) => <div key={`${section.heading}-${sectionIndex}`} style={{ borderTop: "1px solid #e2e8f0", paddingTop: 6 }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{section.heading}</div>
            <div style={{ marginTop: 3, color: "#475569", lineHeight: 1.45 }}>{section.body}</div>
          </div>)}
        </div> : null}
      </article>;
      })}
    </div>
    <div style={{ marginTop: 9, color: "#64748b", fontSize: 12 }}>Reviewed preview content is read-only here; durable output remains tied to its opportunity.</div>
  </section>;
}

function ReviewedOutputArtifactList({ items }: { items: CustomerCaiReadOnlyModel["reviewedOutputArtifacts"] }) {
  const [expandedIndexes, setExpandedIndexes] = React.useState<Set<number>>(() => new Set());
  const toggle = (index: number) => setExpandedIndexes((current) => {
    const next = new Set(current);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    return next;
  });
  if (!items.length) return null;
  return <section data-testid="customer-cai-reviewed-output-artifacts" style={{ ...panel, marginTop: 12 }}>
    <h3 style={{ margin: 0, fontSize: 17 }}>Reviewed output artifacts</h3>
    <div style={{ marginTop: 9, display: "grid", gap: 8 }}>
      {items.map((item, index) => {
        const expanded = expandedIndexes.has(index);
        return <article key={`${item.title}-${index}`} style={compactCard}>
        <div style={{ fontWeight: 800 }}>{item.title}</div>
        <div style={{ marginTop: 4, color: "#1e40af", fontSize: 12, fontWeight: 700 }}>{item.outputType}</div>
        {item.linkedRecommendationTitle ? <div style={{ marginTop: 4, color: "#64748b", fontSize: 12 }}>Linked to: {item.linkedRecommendationTitle}</div> : null}
        <div style={{ marginTop: 6, color: "#475569", lineHeight: 1.45, display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: expanded ? undefined : 3, overflow: expanded ? "visible" : "hidden" }}>{item.summary}</div>
        {item.status ? <div style={{ marginTop: 6, color: "#64748b", fontSize: 12 }}>Status: {item.status}</div> : null}
        {item.priority ? <div style={{ marginTop: 4, color: "#1d4ed8", fontSize: 12, fontWeight: 700 }}>Priority: {item.priority}</div> : null}
        {item.sections.length ? <DisclosureControl expanded={expanded} onToggle={() => toggle(index)} /> : null}
        {expanded && item.sections.length ? <div data-testid={`customer-cai-reviewed-output-artifact-details-${index}`} style={{ marginTop: 8, display: "grid", gap: 7 }}>
          {item.reviewerNote ? <div style={{ color: "#64748b", fontSize: 12 }}>Reviewer note: {item.reviewerNote}</div> : null}
          {item.sections.map((section, sectionIndex) => <div key={`${section.heading}-${sectionIndex}`} style={{ borderTop: "1px solid #e2e8f0", paddingTop: 6 }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{section.heading}</div>
            <div style={{ marginTop: 3, color: "#475569", lineHeight: 1.45 }}>{section.body}</div>
            {section.bullets.length ? <ul style={{ margin: "5px 0 0", paddingLeft: 20, color: "#475569" }}>{section.bullets.map((bullet) => <li key={bullet} style={{ marginTop: 3 }}>{bullet}</li>)}</ul> : null}
          </div>)}
        </div> : null}
      </article>;
      })}
    </div>
    <div style={{ marginTop: 9, color: "#64748b", fontSize: 12 }}>Reviewed artifact content is read-only here; use the related opportunity for its durable brief.</div>
  </section>;
}

export default function CustomerCurrentAssetIntelligence({ account, readModel, state = "ready", presentation = "full" }: Props) {
  if (!isCustomerCaiEligible(account)) return null;

  if (state === "loading") {
    return <section data-testid="customer-cai-surface" aria-label="Current Asset Intelligence"><div style={panel}>Loading Current Asset Intelligence…</div></section>;
  }
  if (state === "error") {
    return <section data-testid="customer-cai-surface" aria-label="Current Asset Intelligence"><div style={panel}>Current Asset Intelligence is temporarily unavailable.</div></section>;
  }

  const model = buildCustomerCaiReadOnlyModel(account, readModel);
  const oePresentationMode = resolveOePresentationMode(account, readModel);
  const compact = presentation === "brief";
  const hasDifferentiatedReviewedAssetData = (model.reviewedInputCount !== null && model.reviewedInputCount > 0)
    || model.priorityOpportunities.length > 0
    || model.assetGaps.length > 0
    || model.recommendedNextActions.length > 0
    || model.outputRecommendations.length > 0
    || model.reviewedOutputPreviews.length > 0
    || model.reviewedOutputArtifacts.length > 0;

  if (compact && !hasDifferentiatedReviewedAssetData) {
    return <section data-testid="customer-cai-surface" aria-label="Current Asset Intelligence" style={{ marginTop: 10 }}>
      <div data-testid="customer-cai-assets-status" style={{ padding: "8px 0", color: "#475569", fontSize: 12, lineHeight: 1.45 }}>
        No CAI opportunity is ready yet. Run or complete an analysis to identify opportunities that CAI can turn into an optimization brief.
      </div>
    </section>;
  }

  return <section data-testid="customer-cai-surface" aria-label="Current Asset Intelligence" style={{ border: "1px solid #bfdbfe", borderRadius: 16, padding: compact ? 12 : 18, background: "#f8fbff", minWidth: 0, maxWidth: "100%", overflowWrap: "anywhere" }}>
    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
      <div style={{ minWidth: 0 }}><div style={{ color: "#2563eb", fontSize: 12, fontWeight: 900, letterSpacing: "0.06em", textTransform: "uppercase" }}>Current Asset Intelligence</div>{!compact ? <h2 style={{ margin: "6px 0 0", fontSize: 23, overflowWrap: "anywhere" }}>Grounded optimization briefs from your current assets</h2> : null}<p style={{ margin: "6px 0 0", color: "#475569", lineHeight: 1.45 }}>Turn an Opportunity Engine recommendation into a grounded optimization brief based on your current assets, evidence, and reviewed proof.</p></div>
    </div>
    <CustomerCaiSummaryFirst model={model} accountStatus={account?.status} compact={compact} />
    {!compact ? <>
      <CustomerCaiOeHandoff mode={oePresentationMode} />
      <section data-testid="customer-cai-priority-opportunities" style={{ ...panel, marginTop: 14 }}><h3 style={{ margin: 0, fontSize: 17 }}>Priority opportunities</h3><div style={{ marginTop: 9 }}><ItemList items={model.priorityOpportunities} empty="No priority opportunities are available in this read model." /></div></section>
      <OutputRecommendationList items={model.outputRecommendations} />
      <ReviewedOutputArtifactList items={model.reviewedOutputArtifacts} />
      <ReviewedOutputPreviewList items={model.reviewedOutputPreviews} />
      <section data-testid="customer-cai-asset-gaps" style={{ ...panel, marginTop: 12 }}><h3 style={{ margin: 0, fontSize: 17 }}>Asset gaps and issues</h3><div style={{ marginTop: 9 }}><ItemList items={model.assetGaps} empty="No specific asset gaps are available in this read model." /></div></section>
      <section data-testid="customer-cai-next-actions" style={{ ...panel, marginTop: 12 }}><h3 style={{ margin: 0, fontSize: 17 }}>Recommended next actions</h3>{model.recommendedNextActions.length ? <ul style={{ margin: "8px 0 0", paddingLeft: 20, color: "#334155" }}>{model.recommendedNextActions.map((action) => <li key={action} style={{ marginTop: 4 }}>{action}</li>)}</ul> : <div style={{ marginTop: 8, color: "#64748b" }}>No recommended next actions are available in this read model.</div>}</section>
    </> : null}
    {!compact ? <div style={{ marginTop: 12, color: "#64748b", fontSize: 12 }}>Account insights remain read-only here; durable CAI actions are available from the selected opportunity.</div> : null}
  </section>;
}
